// Version 4, G-05: Fußzeile übersichtlicher. Wird nur aus src/components/Footer.jsx für version === "4" gerendert.
// Gleiche Inhalte und Links wie bisher (Spalten, Kontakt, Kanäle, Social Media, Rechtliches), nur neu geordnet:
//   1. Kopf: Marke mit Claim links, Kontakt mit Anfrage rechts
//   2. Seitenübersicht: Solutions und Produkte und Anlagen mit ihren Untergruppen nebeneinander, Wissen und Unternehmen gestapelt
//      (unter 768 px Akkordeon wie bisher)
//   3. Kanäle und Social Media in einer Zeile, 4. Rechtliches mit Versionsanzeige. Großer PARTO Schriftzug bleibt.
import { ArrowUpRight, ChevronDown, Mail, Phone, Linkedin, Instagram, Facebook, Youtube } from "lucide-react";
import { Link } from "@/components/LocalizedLink";
import { Reveal } from "@/components/motion";
import BrandLogo from "@/components/relaunch/BrandLogo";
import "@/v4/css/footer-v4.css";

const SOCIAL_ICON = { linkedin: Linkedin, instagram: Instagram, facebook: Facebook, youtube: Youtube };

// Linkliste mit „heading“ Einträgen in Gruppen zerlegen: [{ heading, links }]
const groupsOf = (links) => {
    const out = [];
    links.forEach((l) => {
        if (l.heading) out.push({ heading: l.heading, links: [] });
        else {
            if (!out.length) out.push({ heading: null, links: [] });
            out[out.length - 1].links.push(l);
        }
    });
    return out;
};

const LinkList = ({ links, testPrefix }) => (
    <ul className="v4-foot__list v3-flist">
        {links.map((l, i) => (
            <li key={l.to}>
                <Link to={l.to} data-testid={testPrefix ? `${testPrefix}-${i}` : undefined} className="v4-foot__link v3-ftap">{l.label}</Link>
            </li>
        ))}
    </ul>
);

// Mehrere Untergruppen: ab 1280 px zwei Spalten ohne Lücken (Gruppe 1 und 3 links, Gruppe 2 rechts).
// Darunter lösen sich die Spaltenhüllen auf (display: contents), order stellt die ursprüngliche Reihenfolge her.
const Group = ({ g, gi, testPrefix }) => (
    <div className="v4-foot__group" style={{ order: gi }}>
        {g.heading && <h3 className="v4-foot__sub">{g.heading}</h3>}
        <LinkList links={g.links} testPrefix={testPrefix} />
    </div>
);

const Groups = ({ c }) => {
    const gs = groupsOf(c.links);
    if (gs.length < 2) {
        return <div className="v4-foot__groups">{gs.map((g, gi) => <Group key={g.heading || gi} g={g} gi={gi} testPrefix={c.testPrefix} />)}</div>;
    }
    const cols = [gs.map((g, gi) => [g, gi]).filter(([, gi]) => gi % 2 === 0), gs.map((g, gi) => [g, gi]).filter(([, gi]) => gi % 2 === 1)];
    return (
        <div className="v4-foot__groups is-multi">
            {cols.map((list, ci) => (
                <div key={ci} className="v4-foot__gcol">
                    {list.map(([g, gi]) => <Group key={g.heading || gi} g={g} gi={gi} testPrefix={c.testPrefix} />)}
                </div>
            ))}
        </div>
    );
};

export default function FooterV4({ cols, narrow, tagline, v3f, ctaTo, ctaLabel, telHref, site, channels, channelsTitle, social, socialTitle, socialNote, year, rights, legal, siteVersion, colTitleCls }) {
    // Spalten: 0 Solutions, 1 Produkte und Anlagen, 2 Wissen, 3 Unternehmen (Reihenfolge aus Footer.jsx)
    const area = ["sol", "prod", "wis", "unt"];
    return (
        <footer className="site-footer on-deep relative overflow-hidden v4-foot" data-testid="main-footer" data-footer-version="4">
            <div className="site-footer__content relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-14 md:pt-20 pb-6 md:pb-8">
                {/* 1. Kopf */}
                <div className="v4-foot__top">
                    <div className="v4-foot__brand">
                        <Link to="/" className="inline-flex" data-testid="footer-logo-link" aria-label="Parto Solution">
                            <img src="/brand/parto-logo-negativ.svg" alt="Parto Solution" className="h-12 w-auto" width={192} height={48} loading="lazy" decoding="async" />
                        </Link>
                        <p className="v4-foot__claim">{tagline}</p>
                        <p lang="en" data-testid="footer-v3-claim-en" className="v4-foot__claim-en">{v3f.claimEn}</p>
                    </div>
                    <div className="v4-foot__contact">
                        <h2 className={colTitleCls}>{v3f.contact}</h2>
                        <ul className="v4-foot__contact-list v3-flist" data-testid="footer-v3-contact">
                            <li><a href={telHref} data-testid="footer-phone-link" className="v4-foot__link v3-ftap is-icon"><Phone size={16} aria-hidden="true" /> {site.phone}</a></li>
                            <li><a href={`mailto:${site.email}`} data-testid="footer-mail-link" className="v4-foot__link v3-ftap is-icon"><Mail size={16} aria-hidden="true" /> {site.email}</a></li>
                        </ul>
                        <Link to={ctaTo} data-testid="footer-cta-link" className="rl-btn rl-btn-primary v4-foot__cta">
                            {ctaLabel} <ArrowUpRight size={15} className="arrow" />
                        </Link>
                    </div>
                </div>

                {/* 2. Seitenübersicht */}
                <nav className="v4-foot__nav" aria-label="Seitenübersicht">
                    {narrow ? (
                        <div className="v4-foot__acc" data-testid="footer-v3-accordion">
                            {cols.map((c, ci) => (
                                <details key={c.title} className="v3-facc v4-foot__facc" data-testid={`footer-col-${ci}`}>
                                    <summary><h2 className={colTitleCls}>{c.title}</h2><ChevronDown size={20} strokeWidth={1.75} aria-hidden="true" /></summary>
                                    <Groups c={c} />
                                </details>
                            ))}
                        </div>
                    ) : (
                        <div className="v4-foot__grid">
                            {cols.map((c, ci) => (
                                <div key={c.title} className={`v4-foot__col is-${area[ci] || "x"}`} data-testid={`footer-col-${ci}`}>
                                    <h2 className={colTitleCls}>{c.title}</h2>
                                    <Groups c={c} />
                                </div>
                            ))}
                        </div>
                    )}
                </nav>

                {/* 3. Kanäle und Social Media */}
                <div className="v4-foot__meta" data-testid="footer-v3-row">
                    <div>
                        <h2 className={colTitleCls}>{channelsTitle}</h2>
                        <ul className="v4-foot__channels">
                            {channels.map((c, i) => (
                                <li key={c.label}>
                                    <a href={c.href} target="_blank" rel="noreferrer" data-testid={`footer-eco-link-${i}`} aria-label={c.label} className="group v3-ftap inline-flex items-center gap-2 opacity-85 hover:opacity-100 transition-opacity">
                                        <BrandLogo name={c.label} tone="dark" className={c.label === "BuyParto" ? "h-8" : "h-6"} /> <ArrowUpRight size={13} className="text-white/55 group-hover:text-white" />
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div data-testid="footer-v3-social" className="v4-foot__social">
                        <h2 className={colTitleCls}>{socialTitle}</h2>
                        <ul className="flex flex-wrap gap-2 m-0 p-0 list-none">
                            {social.map((sc) => {
                                const Icon = SOCIAL_ICON[sc.id];
                                return (
                                    <li key={sc.id}>
                                        {!sc.href ? (
                                            <span role="link" aria-disabled="true" aria-label={`${sc.label} ${socialNote}`} title={`${sc.label} ${socialNote}`} data-testid={`footer-social-${sc.id}`} className="v3-social is-missing">
                                                <Icon size={18} aria-hidden="true" />
                                            </span>
                                        ) : (
                                            <a href={sc.href} target="_blank" rel="noreferrer" aria-label={sc.label} title={sc.label} data-testid={`footer-social-${sc.id}`} className="v3-social">
                                                <Icon size={18} aria-hidden="true" />
                                            </a>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                </div>

                {/* 4. Rechtliches */}
                <div className="v4-foot__legal">
                    <p className="m-0">© Parto Solution GmbH {year}. {rights}<span className="v4-site-version" data-testid="footer-site-version"> · v{siteVersion}</span></p>
                    <ul className="v4-foot__legal-links">
                        {legal.map((l) => (
                            <li key={l.to}><Link to={l.to} data-testid={l.testId} className="v4-foot__link v3-ftap">{l.label}</Link></li>
                        ))}
                    </ul>
                </div>
            </div>

            {/* Großer PARTO Schriftzug wie bisher */}
            <div className="absolute inset-x-0 bottom-0 z-0 overflow-hidden select-none pointer-events-none h-[clamp(3.5rem,14vw,16rem)]" aria-hidden="true">
                <Reveal y={0} viewport={{ once: true, margin: "0px" }} className="absolute inset-x-0 top-0 flex justify-center">
                    <span className="block font-black text-[clamp(5rem,21vw,25rem)] leading-[0.74] tracking-[-0.04em] text-white" style={{ opacity: 0.02 }}>PARTO</span>
                </Reveal>
            </div>
        </footer>
    );
}
